/**
 * CommsPrefSwitches (COMMS-UX-01, 2026-10-09) — the per-customer "how may we
 * contact them" switches, shared by the Reach card's Edit popover on the 360
 * Messages tab. The old bell-icon button (CommsPrefButton) is retired: a
 * cryptic icon was carrying the single most important fact about a customer
 * (Seth: "red bell icon crossed out… makes no sense"). The Reach card now
 * states it in words; these switches are its edit surface.
 *
 * Account-level office preferences, honored by every automation AND mirrored
 * into the consent ledger by the DB sync trigger (self-healing — a save here
 * re-asserts paper/marketing suppressions instead of clobbering them).
 * A customer texting STOP is separate, phone-level, and always final.
 */
import { cn } from "@/lib/cn";
import { useCommsPref, useSaveCommsPref } from "./comms";

function PrefToggle({ label, hint, on, onChange }: { label: string; hint?: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50" title={hint}>
      {label}
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={(e) => { e.preventDefault(); onChange(!on); }}
        className={cn("relative h-5 w-9 shrink-0 rounded-full transition-colors", on ? "bg-rose-500" : "bg-slate-300")}
      >
        <span className={cn("absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all", on ? "left-[18px]" : "left-0.5")} />
      </button>
    </label>
  );
}

/** The five switches + the explainer footer — drop inside any popover/panel. */
export function CommsPrefSwitches({ custno }: { custno: string }) {
  const { data: pref } = useCommsPref(custno);
  const save = useSaveCommsPref();
  const set = (patch: Partial<{ no_sms: boolean; no_email: boolean; no_review_request: boolean; paper_invoice: boolean; no_marketing_email: boolean }>) =>
    save.mutate({
      custno,
      no_sms: pref?.no_sms ?? false,
      no_email: pref?.no_email ?? false,
      no_review_request: pref?.no_review_request ?? false,
      paper_invoice: pref?.paper_invoice ?? false,
      no_marketing_email: pref?.no_marketing_email ?? false,
      ...patch,
    });

  return (
    <>
      <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        Pegasus may…
      </div>
      <PrefToggle label="Never text" on={pref?.no_sms ?? false} onChange={(v) => set({ no_sms: v })} />
      <PrefToggle label="Never email" on={pref?.no_email ?? false} onChange={(v) => set({ no_email: v })} />
      <PrefToggle label="No marketing emails" hint="Newsletters and offers stop; invoices and appointment emails still send" on={pref?.no_marketing_email ?? false} onChange={(v) => set({ no_marketing_email: v })} />
      <PrefToggle label="No review requests" on={pref?.no_review_request ?? false} onChange={(v) => set({ no_review_request: v })} />
      <PrefToggle label="Paper invoices — office mails them" hint="Invoice emails stop; each finalized invoice lands on the To-Mail list on the Invoices page" on={pref?.paper_invoice ?? false} onChange={(v) => set({ paper_invoice: v })} />
      <div className="border-t border-hairline px-3 py-2 text-[11px] leading-relaxed text-slate-400">
        Office preference — honored by every automation and shown to anyone opening this customer.
        A customer texting STOP is separate and always final. Number-level rules live under “per-line details.”
      </div>
    </>
  );
}
